import React from "react"
import toast from "react-hot-toast"
import { useApp } from "../context/AppContext"
import { Icon, useT, useIsMobile } from "../shared"
import { useOrgName } from "../shared/BrandLogo"
import { Stat, TableWrap, Btn, Badge } from "../shared/components"
import { listKBDocs, uploadKBDoc, deleteKBDoc } from "../api/kb.api"
import { getAIFeedbackAnalytics } from "../api/ai.analytics.api"

/* ============================================================
   PRD §FR-10: Knowledge Base Management (RAG document corpus)
   Exposes: KnowledgeBaseScreen
   ============================================================ */

function KnowledgeBaseScreen() {
  const t = useT();
  const { lang, role, navigate, currentProgramId, currentProgram } = useApp();
  const orgName = useOrgName()
  const isMobile = useIsMobile();
  const isStaff = role === "trainer" || role === "admin";
  const [docs, setDocs] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [selected, setSelected] = React.useState(null);
  const [query, setQuery] = React.useState("");
  const fileInput = React.useRef(null);

  const refresh = React.useCallback((silent = false) => {
    if (!currentProgramId) return;
    if (!silent) setLoading(true);
    listKBDocs(currentProgramId)
      .then((data) => {
        setDocs(data);
        if (data.length && !selected) setSelected(data[0].id);
      })
      .catch(() => setDocs([]))
      .finally(() => { if (!silent) setLoading(false); });
  }, [currentProgramId, selected]);

  React.useEffect(() => { refresh(); }, [currentProgramId]);

  // Auto-poll every 4 s while any doc is still indexing (skip errored); stops when all done
  React.useEffect(() => {
    const hasIndexing = docs.some((d) => !d.embedded_at && !d.embed_error);
    if (!hasIndexing) return;
    const timer = setInterval(() => {
      listKBDocs(currentProgramId)
        .then((data) => {
          setDocs(data);
          const wasIndexing = docs.some((d) => !d.embedded_at);
          const nowDone    = data.every((d) => d.embedded_at);
          if (wasIndexing && nowDone) {
            toast.success(lang === "id" ? "Semua dokumen selesai diindex ✓" : "All documents indexed ✓");
          }
        })
        .catch(() => {});
    }, 4000);
    return () => clearInterval(timer);
  }, [docs, currentProgramId, lang]);

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file || !currentProgramId) return;
    setUploading(true);
    try {
      await uploadKBDoc(currentProgramId, file);
      toast.success(lang === "id" ? "Dokumen terunggah, sedang diindex…" : "Uploaded, indexing…");
      setTimeout(() => refresh(true), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message);
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  async function handleDelete(docId) {
    if (!confirm(lang === "id" ? "Hapus dokumen ini?" : "Delete this document?")) return;
    try {
      await deleteKBDoc(currentProgramId, docId);
      toast.success(lang === "id" ? "Dihapus" : "Deleted");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message);
    }
  }

  function extOf(mime) {
    if (!mime) return "txt";
    if (mime.includes("pdf")) return "pdf";
    if (mime.includes("word") || mime.includes("officedocument")) return "docx";
    return "txt";
  }

  const totalChunks = docs.reduce((s, d) => s + (d._count?.chunks || 0), 0);
  const totalKB = docs.reduce((s, d) => s + (d.file_size || 0), 0) / 1024;
  const filtered = docs.filter((d) => !query || d.filename.toLowerCase().includes(query.toLowerCase()));
  const cur = docs.find((d) => d.id === selected) || docs[0];

  return (
    <div className="main-inner">
      <div className="page-header">
        <div className="page-header-text">
          <div className="eyebrow">
            {currentProgram?.code?.toUpperCase() || orgName} · {lang === "id" ? "Knowledge Base" : "Knowledge Base"}
          </div>
          <h1 className="h1" style={{ marginTop: 6 }}>{t.knowledgeBase}</h1>
          <div className="muted mt-1">
            {lang==="id"
              ? "Dokumen sebagai satu-satunya sumber jawaban Asisten AI. Tanpa akses internet."
              : "Documents as the only source for the AI Assistant. No internet access."}
          </div>
        </div>
        {isStaff && (
          <div className="page-header-actions">
            <input ref={fileInput} type="file" hidden onChange={handleUpload} accept=".pdf,.txt,.docx,.md"/>
            <Btn
              variant="primary"
              icon={<Icon name="upload" size={14}/>}
              disabled={uploading}
              onClick={() => fileInput.current?.click()}
            >
              {uploading ? (lang === "id" ? "Sedang upload…" : "Uploading…") : (lang === "id" ? "Upload dokumen" : "Upload document")}
            </Btn>
          </div>
        )}
      </div>

      {/* KB stats */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
        <Stat label={lang==="id"?"Total dokumen":"Total documents"} value={String(docs.length)} hint={`${docs.filter(d=>d.embedded_at).length} ${lang==="id"?"siap":"ready"}`}/>
        <Stat label={lang==="id"?"Total chunk":"Total chunks"} value={totalChunks.toLocaleString()} hint="pgvector · 768-d"/>
        <Stat label={lang==="id"?"Total ukuran":"Total size"} value={totalKB > 1024 ? `${(totalKB/1024).toFixed(1)} MB` : `${totalKB.toFixed(1)} KB`} hint="PDF · DOCX · TXT"/>
        <Stat label={lang==="id"?"Provider":"Provider"} value="Gemini" hint="text-embedding"/>
      </div>

      <div className="grid" style={{gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr", gap: 16}}>
        {/* Document list */}
        <div className="tile">
          <div className="tile-header">
            <h3>{lang==="id"?"Dokumen Knowledge Base":"Knowledge Base documents"}</h3>
            <div className="row gap-2">
              <div className="row gap-2" style={{padding: "0 8px", background:"var(--g-10)", border:"1px solid var(--border-strong)", height: 28}}>
                <Icon name="search" size={12} color="var(--text-tertiary)"/>
                <input value={query} onChange={e=>setQuery(e.target.value)}
                  placeholder={lang==="id"?"Cari di seluruh dokumen…":"Search across all docs…"}
                  style={{border:0, background:"transparent", outline:"none", width: "100%", maxWidth: 200, fontSize: 12}}/>
              </div>
            </div>
          </div>
          <TableWrap>
          <table className="table table-compact">
            <thead><tr>
              <th>{lang==="id"?"Dokumen":"Document"}</th>
              <th>{lang==="id"?"Ukuran":"Size"}</th>
              <th>Chunks</th>
              <th>{lang==="id"?"Status":"Status"}</th>
              <th>{lang==="id"?"Ditambahkan":"Added"}</th>
              {isStaff && <th></th>}
            </tr></thead>
            <tbody>
              {loading && (
                <tr><td colSpan={isStaff ? 6 : 5} className="muted small" style={{textAlign:"center", padding:20}}>
                  {lang==="id"?"Memuat…":"Loading…"}
                </td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={isStaff ? 6 : 5} className="muted small" style={{textAlign:"center", padding:20}}>
                  {lang==="id"?"Belum ada dokumen. Klik \"Upload dokumen\" untuk menambah.":"No documents. Click \"Upload document\" to add."}
                </td></tr>
              )}
              {filtered.map((d) => {
                const type = extOf(d.mime_type);
                const sizeKB = d.file_size ? (d.file_size / 1024).toFixed(1) : "—";
                const added = new Date(d.created_at).toLocaleDateString(lang === "id" ? "id-ID" : "en-US");
                const isReady = !!d.embedded_at;
                const hasError = !!d.embed_error;
                return (
                  <tr key={d.id} onClick={()=>setSelected(d.id)} style={{cursor:"pointer", background: selected === d.id ? "var(--blue-10)" : "transparent"}}>
                    <td>
                      <div className="row gap-2">
                        <DocIcon type={type}/>
                        <div>
                          <div className="small mono" style={{fontWeight: 500}}>{d.filename}</div>
                          <div className="tiny muted">{lang==="id"?"oleh":"by"} {d.uploader?.full_name || "—"}</div>
                          {hasError && (
                            <div className="tiny" style={{color:"var(--red-60)", marginTop:2}} title={d.embed_error}>
                              ⚠ {d.embed_error?.slice(0, 60)}{d.embed_error?.length > 60 ? "…" : ""}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="mono tnum small">{sizeKB} KB</td>
                    <td className="mono tnum small">{d._count?.chunks ?? "—"}</td>
                    <td>
                      {hasError
                        ? <Badge tone="danger" dot>{lang==="id"?"Gagal":"Failed"}</Badge>
                        : isReady
                        ? <Badge tone="success" dot>{lang==="id"?"Siap":"Ready"}</Badge>
                        : <Badge tone="brand" dot>{lang==="id"?"Diindex…":"Indexing…"}</Badge>}
                    </td>
                    <td className="tiny muted">{added}</td>
                    {isStaff && (
                      <td>
                        <div className="row gap-1" style={{justifyContent: "flex-end"}}>
                          <Btn variant="ghost" size="sm" icon={<Icon name="trash" size={12}/>} title={lang==="id"?"Hapus":"Delete"} onClick={(e)=>{e.stopPropagation();handleDelete(d.id)}}/>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          </TableWrap>
          {isStaff && (
            <div className="tile-foot">
              <span className="tiny muted">
                {lang==="id"?"Dukung: PDF · DOCX · TXT · maks 500 MB · embedding via Gemini":"Supported: PDF · DOCX · TXT · max 500 MB · embedding via Gemini"}
              </span>
              <Btn variant="secondary" size="sm" icon={<Icon name="upload" size={12}/>} onClick={() => fileInput.current?.click()}>{lang==="id"?"Tambah":"Add"}</Btn>
            </div>
          )}
        </div>

        {/* Side: doc detail + AI test */}
        <div className="col gap-3">
          {/* Doc detail */}
          <div className="tile">
            <div className="tile-header"><h3>{lang==="id"?"Detail dokumen":"Document detail"}</h3></div>
            <div style={{padding: "16px"}}>
              {cur ? (
                <>
                  <div className="row gap-3 mb-3">
                    <DocIcon type={extOf(cur.mime_type)} big/>
                    <div style={{minWidth: 0, flex: 1}}>
                      <div className="small mono" style={{fontWeight: 500, wordBreak: "break-all"}}>{cur.filename}</div>
                      <div className="tiny muted mt-1">{cur.file_size ? `${(cur.file_size / 1024).toFixed(1)} KB` : "—"}</div>
                    </div>
                  </div>
                  <div className="divider"/>
                  <div className="col gap-2 small">
                    <div className="row between"><span className="muted">Chunks</span><span className="mono tnum">{cur._count?.chunks ?? 0}</span></div>
                    <div className="row between"><span className="muted">Status</span>
                      <span className="mono">{cur.embedded_at ? (lang==="id"?"Siap":"Ready") : (lang==="id"?"Diindex":"Indexing")}</span>
                    </div>
                    <div className="row between"><span className="muted">{lang==="id"?"Ditambahkan":"Added"}</span>
                      <span className="mono">{new Date(cur.created_at).toLocaleDateString(lang==="id"?"id-ID":"en-US")}</span>
                    </div>
                    <div className="row between"><span className="muted">{lang==="id"?"Diupload oleh":"Uploaded by"}</span>
                      <span className="mono">{cur.uploader?.full_name || "—"}</span>
                    </div>
                  </div>
                  {isStaff && (
                    <div className="row gap-2 mt-4">
                      {cur.storage_url && (
                        <a className="btn sm secondary" style={{flex:1, textDecoration:"none"}} href={cur.storage_url} target="_blank" rel="noopener noreferrer">
                          <Icon name="download" size={12}/>{lang==="id"?"Lihat":"View"}
                        </a>
                      )}
                      <button className="btn sm ghost icon-only" onClick={()=>handleDelete(cur.id)}><Icon name="trash" size={12}/></button>
                    </div>
                  )}
                </>
              ) : (
                <p className="muted small" style={{textAlign:"center", padding:24}}>
                  {lang==="id"?"Pilih dokumen untuk melihat detail":"Select a document to view details"}
                </p>
              )}
            </div>
          </div>

          {/* AI guardrails reminder */}
          <div className="tile" style={{background: "#0B1417", color: "white", border: "none"}}>
            <div style={{padding: 16}}>
              <div className="row gap-2 mb-2">
                <Icon name="ai" color="var(--blue-50)"/>
                <strong style={{fontSize: 14}}>{lang==="id"?"Aturan AI":"AI guardrails"}</strong>
              </div>
              <div className="col gap-2" style={{fontSize: 12, color: "#D6DAD9", lineHeight: 1.5}}>
                <div className="row gap-2"><Icon name="check" size={12} color="var(--green-50)"/><span>{lang==="id"?"Hanya jawab dari dokumen course ini":"Answer only from this course's docs"}</span></div>
                <div className="row gap-2"><Icon name="check" size={12} color="var(--green-50)"/><span>{lang==="id"?"Setiap jawaban menyertakan kutipan & halaman":"Every answer cites filename + page"}</span></div>
                <div className="row gap-2"><Icon name="check" size={12} color="var(--green-50)"/><span>{lang==="id"?"Jika tidak ditemukan → \"tidak tersedia\"":"If not found → reply \"not available\""}</span></div>
                <div className="row gap-2"><Icon name="close" size={12} color="var(--red-50)"/><span>{lang==="id"?"Tidak ada akses internet / general knowledge":"No internet / general knowledge"}</span></div>
              </div>
            </div>
          </div>

          {/* Test query */}
          <div className="tile">
            <div className="tile-header"><h3>{lang==="id"?"Uji kueri AI":"Test AI query"}</h3></div>
            <div style={{padding: 16}}>
              <p className="tiny muted mb-3" style={{margin:"0 0 12px"}}>
                {lang==="id"?"Coba pertanyaan untuk memastikan AI menemukan jawaban di KB.":"Try a question to verify the AI finds an answer in the KB."}
              </p>
              <textarea
                placeholder={lang==="id"?"Mis: Pada arus berapa boom inflatable tidak boleh dipakai?":"e.g. At what current is an inflatable boom unsafe?"}
                style={{width:"100%", minHeight: 64, padding: "8px 10px", border: "1px solid var(--border-strong)", outline:"none", fontFamily: "inherit", fontSize: 13, resize: "vertical"}}
              />
              <button className="btn sm mt-3" style={{width:"100%"}} onClick={()=>navigate("ai")}><Icon name="ai" size={12}/>{lang==="id"?"Buka di Asisten AI":"Open in AI Assistant"}</button>
            </div>
          </div>
        </div>
      </div>

      {/* FR-26: AI Feedback Analytics — full-width, staff only */}
      {isStaff && <AIFeedbackPanel />}
    </div>
  );
}

/* ============================================================
   FR-26: AI Feedback Analytics panel (admin/trainer only)
   ============================================================ */
function AIFeedbackPanel() {
  const { lang, currentProgramId } = useApp()
  const isMobile = useIsMobile()
  const [data, setData]       = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const [open, setOpen]       = React.useState(false)

  React.useEffect(() => {
    if (!open || !currentProgramId) return
    setLoading(true)
    getAIFeedbackAnalytics(currentProgramId)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [open, currentProgramId])

  const pct = (n, d) => (d > 0 ? Math.round((n / d) * 100) : 0)

  return (
    <div className="tile mt-4">
      <div
        className="tile-header"
        style={{ cursor: "pointer" }}
        onClick={() => setOpen((o) => !o)}
      >
        <h3>
          <Icon name="chart" size={14} style={{ marginRight: 8 }} />
          {lang === "id" ? "Analitik Feedback AI" : "AI Feedback Analytics"}
        </h3>
        <Icon name={open ? "chevron_up" : "chevron_down"} size={14} />
      </div>

      {open && (
        <div style={{ padding: "0 20px 20px" }}>
          {loading && (
            <p className="muted small" style={{ textAlign: "center", padding: 24 }}>
              {lang === "id" ? "Memuat…" : "Loading…"}
            </p>
          )}

          {!loading && !data && (
            <p className="muted small" style={{ textAlign: "center", padding: 24 }}>
              {lang === "id" ? "Tidak ada data." : "No data available."}
            </p>
          )}

          {!loading && data && (() => {
            const s = data.summary || {}
            const upPct = pct(s.upVotes, (s.upVotes || 0) + (s.downVotes || 0))
            return (
              <>
                {/* Summary stats */}
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(3, 1fr)" : "repeat(5, 1fr)", gap: 12, marginBottom: 20 }}>
                  <Stat label={lang === "id" ? "Percakapan" : "Conversations"} value={String(s.totalConversations ?? 0)} />
                  <Stat label={lang === "id" ? "Pesan AI" : "AI Messages"} value={String(s.totalAssistantMessages ?? 0)} />
                  <Stat label="👍 Up" value={String(s.upVotes ?? 0)} />
                  <Stat label="👎 Down" value={String(s.downVotes ?? 0)} />
                  <Stat label={lang === "id" ? "Rating +" : "Approval"} value={`${upPct}%`} hint={`${s.ratedPct ?? 0}% ${lang === "id" ? "dirating" : "rated"}`} />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
                  {/* Top-rated answers */}
                  <div>
                    <div className="small" style={{ fontWeight: 600, marginBottom: 10 }}>
                      👍 {lang === "id" ? "Jawaban Terbaik" : "Top-Rated Answers"}
                    </div>
                    {(data.topAnswers || []).length === 0 && (
                      <p className="tiny muted">{lang === "id" ? "Belum ada." : "None yet."}</p>
                    )}
                    {(data.topAnswers || []).map((a, i) => (
                      <div key={a.id || i} className="tile" style={{ padding: "10px 14px", marginBottom: 8, background: "var(--surface-2)" }}>
                        <div className="tiny muted mb-1">
                          {a.conversation?.participant?.full_name || "—"} · {new Date(a.created_at).toLocaleDateString(lang === "id" ? "id-ID" : "en-US")}
                        </div>
                        <div className="small mono" style={{ marginBottom: 6, fontWeight: 500 }}>
                          Q: {(a.conversation?.messages?.find(m => m.role === "user")?.content || "").slice(0, 80) || a.question || "—"}
                        </div>
                        <div className="tiny" style={{ color: "var(--text-2)", lineHeight: 1.5 }}>
                          {(a.content || "").slice(0, 120)}{(a.content || "").length > 120 ? "…" : ""}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Low-rated answers */}
                  <div>
                    <div className="small" style={{ fontWeight: 600, marginBottom: 10 }}>
                      👎 {lang === "id" ? "Jawaban Perlu Perbaikan" : "Needs Improvement"}
                    </div>
                    {(data.lowAnswers || []).length === 0 && (
                      <p className="tiny muted">{lang === "id" ? "Belum ada." : "None yet."}</p>
                    )}
                    {(data.lowAnswers || []).map((a, i) => (
                      <div key={a.id || i} className="tile" style={{ padding: "10px 14px", marginBottom: 8, background: "var(--red-10)", border: "1px solid var(--red-20)" }}>
                        <div className="tiny muted mb-1">
                          {a.conversation?.participant?.full_name || "—"} · {new Date(a.created_at).toLocaleDateString(lang === "id" ? "id-ID" : "en-US")}
                        </div>
                        <div className="small mono" style={{ marginBottom: 6, fontWeight: 500 }}>
                          Q: {(a.conversation?.messages?.find(m => m.role === "user")?.content || "").slice(0, 80) || a.question || "—"}
                        </div>
                        <div className="tiny" style={{ color: "var(--text-2)", lineHeight: 1.5 }}>
                          {(a.content || "").slice(0, 120)}{(a.content || "").length > 120 ? "…" : ""}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Top cited docs */}
                {(data.topDocs || []).length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <div className="small" style={{ fontWeight: 600, marginBottom: 10 }}>
                      📄 {lang === "id" ? "Dokumen Paling Sering Dikutip" : "Most Cited Documents"}
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {data.topDocs.map((d, i) => (
                        <div key={i} className="tile" style={{ padding: "6px 12px", display: "flex", alignItems: "center", gap: 8, background: "var(--surface-2)" }}>
                          <span className="tiny mono" style={{ fontWeight: 600 }}>{d.filename}</span>
                          <Badge tone="brand">{d.count}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )
          })()}
        </div>
      )}
    </div>
  )
}

function DocIcon({ type, big }) {
  const colors = {
    pdf:  { bg: "var(--red-10)",   fg: "var(--red-60)",   label: "PDF" },
    docx: { bg: "var(--blue-10)",  fg: "var(--blue-70)",  label: "DOCX" },
    txt:  { bg: "var(--g-10)",     fg: "var(--g-70)",     label: "TXT" },
  };
  const c = colors[type] || colors.txt;
  const size = big ? 48 : 28;
  return (
    <div style={{
      width: size, height: size, background: c.bg, color: c.fg,
      display: "grid", placeItems: "center", border: "1px solid var(--border)",
      fontFamily: "var(--font-mono)", fontWeight: 600, fontSize: big ? 10 : 8, flexShrink: 0,
    }}>{c.label}</div>
  );
}

export default KnowledgeBaseScreen
